/**
 * O que a área de coleções aceita no upload.
 *
 * Fonte única: o `accept` do seletor de arquivos, a validação da fila e o texto
 * que a tela mostra ao usuário saem todos daqui. Antes o `accept` era uma string
 * escrita à mão na página, separada da lista que a fila validava, e a interface
 * não dizia em lugar nenhum o que era aceito.
 */
export const MIME_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv': 'text/csv',
};

export const ACCEPTED_TYPES = new Set(Object.values(MIME_BY_EXTENSION));
export const ACCEPTED_EXTENSIONS = Object.keys(MIME_BY_EXTENSION);

/** Mesmo teto do backend para upload via URL assinada (RAG_PRESIGN_MAX_MB, ADR-048). */
export const MAX_FILE_SIZE_MB = 500;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

/** Valor do atributo `accept` do <input type="file">. */
export const FILE_INPUT_ACCEPT = [...ACCEPTED_EXTENSIONS, ...ACCEPTED_TYPES].join(',');

/** Rótulo curto das extensões aceitas, para a interface: "PDF, XLSX ou CSV". */
export const ACCEPTED_LABEL = formatExtensionList(ACCEPTED_EXTENSIONS);

function formatExtensionList(extensions: string[]): string {
  const names = extensions.map((ext) => ext.replace('.', '').toUpperCase());
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ou ${names.at(-1)}`;
}

export function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_TYPES.has(file.type)) return true;
  // Fallback por extensão — alguns navegadores não setam o MIME certo.
  return ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
}

/** Resolve o content_type exigido pelo presign — mesmo fallback de isAcceptedFile. */
export function resolveContentType(file: File): string {
  if (ACCEPTED_TYPES.has(file.type)) return file.type;
  const name = file.name.toLowerCase();
  const ext = ACCEPTED_EXTENSIONS.find((candidate) => name.endsWith(candidate));
  const mime = ext ? MIME_BY_EXTENSION[ext] : undefined;
  return mime ?? (file.type || 'application/octet-stream');
}
